// ESI's content security policies (openspec change content-security-policy): one definition for
// CloudFront (the web app and the sandbox page), the API and the authorization server, and the
// local preview.

/**
 * The one switch (design §6): `true` enforces every policy (`Content-Security-Policy`), `false`
 * sends them as `Content-Security-Policy-Report-Only`. Enforced since #121, once dev had
 * delivered reports from Firefox and Chromium with no violation from the app itself.
 */
export const CSP_ENFORCE = true;

/** The header that carries the policies in the chosen mode. */
export function cspHeaderName(enforce: boolean = CSP_ENFORCE) {
  return enforce ? "Content-Security-Policy" : "Content-Security-Policy-Report-Only";
}

/** Where browsers post violation reports; relative in `report-uri`, so each stage reports to itself. */
export const CSP_REPORT_PATH = "/api/csp-reports";
/** The `report-to` group, declared by `Reporting-Endpoints`. */
export const CSP_REPORT_GROUP = "csp";

/**
 * `Reporting-Endpoints` value naming the report endpoint at the stage's public origin (#121):
 * Chromium delivers `report-to` reports only to an absolute URL. `origin` comes from the stage's
 * configuration, never from a request; without one (a stage with no configured domain), the
 * relative path, which Firefox and Safari still reach through `report-uri`.
 */
export function cspReportingEndpoints(origin?: string): string {
  if (origin === undefined) {
    return `${CSP_REPORT_GROUP}="${CSP_REPORT_PATH}"`;
  }
  const url = new URL(origin);
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error(`the CSP report origin must be http(s): ${origin}`);
  }
  return `${CSP_REPORT_GROUP}="${url.origin}${CSP_REPORT_PATH}"`;
}

type Directives = Record<string, readonly string[]>;

/** `report-uri` for Firefox and Safari, `report-to` for Chromium (which then ignores the first). */
const REPORTING: Directives = {
  "report-uri": [CSP_REPORT_PATH],
  "report-to": [CSP_REPORT_GROUP],
};

function serialise(directives: Directives): string {
  return Object.entries({ ...directives, ...REPORTING })
    .map(([name, sources]) => [name, ...sources].join(" "))
    .join("; ");
}

/**
 * The web app's policy (design §2). Inline styles: Radix and React insert `<style>` elements at
 * runtime, and a static header cannot carry a nonce. `data:` images: the TOTP QR code; `blob:`:
 * sanitised SVG shown as images. `connectOrigins`: the stage's documents bucket (presigned
 * uploads go straight to it).
 */
export function sitePolicy(options: { connectOrigins?: readonly string[] } = {}): string {
  return serialise({
    "default-src": ["'self'"],
    "script-src": ["'self'"],
    "style-src": ["'self'", "'unsafe-inline'"],
    "img-src": ["'self'", "blob:", "data:"],
    "font-src": ["'self'"],
    "connect-src": ["'self'", ...(options.connectOrigins ?? [])],
    "frame-src": ["'self'"],
    "object-src": ["'none'"],
    "base-uri": ["'none'"],
    "form-action": ["'self'"],
    "frame-ancestors": ["'none'"],
  });
}

/**
 * The console website's policy (operator-identity-pool D1): nothing by default, ESI's own
 * scripts, styles, fonts and images, and requests to its own origin only (the console reads
 * metadata and never uploads or downloads tenant objects). Inline styles, `data:` and `blob:`
 * images for the same reasons as {@link sitePolicy}. No frames at all: the console host serves
 * no sandbox page, so diagrams under a support grant are not drawn there.
 */
export function consoleSitePolicy(): string {
  return serialise({
    "default-src": ["'none'"],
    "script-src": ["'self'"],
    "style-src": ["'self'", "'unsafe-inline'"],
    "img-src": ["'self'", "blob:", "data:"],
    "font-src": ["'self'"],
    "manifest-src": ["'self'"],
    "connect-src": ["'self'"],
    "object-src": ["'none'"],
    "base-uri": ["'none'"],
    "form-action": ["'self'"],
    "frame-ancestors": ["'none'"],
  });
}

/** The sandbox page's own policy, as its `<meta>` tag carries it (enforced in every mode). */
const SANDBOX_DOCUMENT: Directives = {
  "default-src": ["'none'"],
  "script-src": ["'self'"],
  "style-src": ["'unsafe-inline'"],
  "img-src": ["'self'", "data:"],
  "base-uri": ["'none'"],
  "form-action": ["'none'"],
};

/** The `<meta http-equiv="Content-Security-Policy">` of the sandbox page (`artefacts-viewer`). */
export const SANDBOX_META_POLICY = Object.entries(SANDBOX_DOCUMENT)
  .map(([name, sources]) => [name, ...sources].join(" "))
  .join("; ");

/**
 * `/sandbox/*` (the Mermaid frame of `artefacts-viewer`): the page's own `<meta>` policy, plus
 * what `<meta>` cannot set: framing by ESI only.
 */
export const SANDBOX_POLICY = serialise({
  ...SANDBOX_DOCUMENT,
  "frame-ancestors": ["'self'"],
});

/** The API's JSON responses. */
export const API_POLICY = serialise({
  "default-src": ["'none'"],
  "frame-ancestors": ["'none'"],
  "base-uri": ["'none'"],
});

/**
 * The authorization server's responses. `script-src 'none'` exists so the provider can append
 * the hash of its `form_post` script to it (browsers then ignore `'none'`).
 */
export const PROVIDER_POLICY = serialise({
  "default-src": ["'none'"],
  "script-src": ["'none'"],
  "frame-ancestors": ["'none'"],
  "base-uri": ["'none'"],
});
